import {
  IconBasketDiscount,
  IconCurrencyDollar,
  IconTicket,
  IconWalk,
} from '@tabler/icons-react'
import ReactEcharts from 'echarts-for-react'
import { useEffect } from 'react'

import {
  ContentCard,
  Leaderboard,
  MetricCard,
  ScalrBoxView,
  ScalrCarousel,
} from '@/components/customComponents'
import { MasterChart } from '@/components/ui/charts'
import KPIGoalChart from '@/components/ui/charts/KpiGoalChart'

import { metricCardColors } from '@/constants/COLOR_PALETTE'
import { toast, useSalesMetrics, useSalesRegionBoxMetrics } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { KpiGoalsModel } from '@/types/clarity'
import { dateToString, type DateType } from '@/utils'

export default function SalesPage({
  kpiGoals,
  kpiGoalsLoading,
}: {
  kpiGoals: KpiGoalsModel | undefined
  kpiGoalsLoading: boolean
}) {
  const { selectedBranch, date, dateFilterType } = useReduxBaseFilter()

  const refreshTrigger = useAppSelector(selectPageRefreshTrigger('sales'))

  const { companyData, chartData, metricCardData, loading, error, refetch } =
    useSalesMetrics(
      dateToString(date, 'from'),
      dateToString(date, 'to'),
      dateFilterType === 'test-range'
        ? 'month-to-date'
        : (dateFilterType as DateType),
      selectedBranch
    )

  // Fetch region box metrics
  const {
    processedData,
    loading: regionCardsLoading,
    error: regionCardsError,
    refetch: regionCardsRefetch,
  } = useSalesRegionBoxMetrics(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType as DateType
  )

  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing sales page data...')
      refetch()
      regionCardsRefetch()
    }
  }, [refreshTrigger, refetch, regionCardsRefetch])

  useEffect(() => {
    if (error) {
      toast({
        title: 'Something went wrong.',
        description: error.message,
        variant: 'destructive',
      })
    }
  }, [error, regionCardsError])

  const {
    itemCountMixData,
    itemCountMixColumns,
    itemCountOrderedMixData,
    itemCountOrderedMixColumns,
    categoryCountMixOptions,
    salesInAWeekOptions,
    modifierCountMixData,
    modifierCountMixColumns,
  } = chartData

  return (
    <div className="space-y-6">
      <div className="space-y-3 mb-10">
        <ScalrCarousel>
          <MetricCard
            title="ADNS"
            mtdCurrContent={metricCardData?.currMetricsMonthly?.adns}
            ytdCurrContent={metricCardData?.currMetricsYearly?.adns}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.adns}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.adns}
            content={companyData?.adns?.toFixed(2)}
            type="money"
            subTitle="Average Daily Sales"
            kpiGoal={kpiGoals?.netSales}
            loading={loading || kpiGoalsLoading}
            icon={<IconCurrencyDollar color="white" size={20} />}
            color={metricCardColors.red500}
          />
          <MetricCard
            title="Traffic"
            mtdCurrContent={metricCardData?.currMetricsMonthly?.traffic}
            ytdCurrContent={metricCardData?.currMetricsYearly?.traffic}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.traffic}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.traffic}
            content={companyData?.traffic?.toFixed(0)}
            type="number"
            kpiGoal={kpiGoals?.totalTraffic}
            loading={loading || kpiGoalsLoading}
            icon={<IconWalk color="white" size={20} />}
            color={metricCardColors.blue500}
          />
          <MetricCard
            title="AoV"
            mtdCurrContent={metricCardData?.currMetricsMonthly?.avgCheck}
            ytdCurrContent={metricCardData?.currMetricsYearly?.avgCheck}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.avgCheck}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.avgCheck}
            content={companyData?.avgCheck.toFixed(2)}
            type="money"
            subTitle="Average Order Value"
            kpiGoal={kpiGoals?.aov}
            loading={loading || kpiGoalsLoading}
            icon={<IconTicket color="white" size={20} />}
            color={metricCardColors.green500}
          />
          <MetricCard
            title="Discounts"
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.totalDiscountsRatio
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.totalDiscountsRatio
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.totalDiscountsRatio
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.totalDiscountsRatio
            }
            content={companyData?.totalDiscountsRatio?.toFixed(0)}
            type="percentage"
            kpiGoal={kpiGoals?.discount}
            loading={loading || kpiGoalsLoading}
            icon={<IconBasketDiscount color="white" size={20} />}
            color={metricCardColors.orange500}
            isIncreasingPositive={false}
          />
        </ScalrCarousel>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Leaderboard
          title="Best Sellers"
          data={itemCountMixData?.slice(0, 5)}
          columns={itemCountMixColumns}
          sortBy="itemMixPercentage"
          loading={!itemCountMixData}
        />

        <Leaderboard
          title="What's Trending?"
          data={itemCountOrderedMixData}
          columns={itemCountOrderedMixColumns}
          sortBy="itemMixPercentage"
          loading={!itemCountOrderedMixData}
        />

        <ContentCard
          title="Category Count Mix"
          loading={!categoryCountMixOptions}
        >
          {categoryCountMixOptions && (
            <ReactEcharts option={categoryCountMixOptions} />
          )}
        </ContentCard>
      </div>

      <ScalrBoxView
        data={processedData}
        loading={regionCardsLoading}
        filterValues={{
          start: dateToString(date, 'from'),
          end: dateToString(date, 'to'),
        }}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ContentCard
          title="Sales in a Week by Shifts"
          loading={!salesInAWeekOptions}
        >
          {salesInAWeekOptions && <ReactEcharts option={salesInAWeekOptions} />}
        </ContentCard>

        <Leaderboard
          title="Best Modifiers"
          data={modifierCountMixData}
          columns={modifierCountMixColumns}
          sortBy="orderPenetration"
          loading={!modifierCountMixData}
        />

        <ContentCard
          title="ADNS vs KPI Goal"
          showViewMore={false}
          loading={loading || kpiGoalsLoading}
        >
          <KPIGoalChart
            // TODO: to be calculated
            trailingGoalData={2250.0}
            currentData={companyData?.adns || 0}
            kpiGoalData={kpiGoals?.netSales || 0}
            unit="money"
          />
        </ContentCard>
      </div>

      <MasterChart
        chartType="sales"
        dateRange={date}
        selectedBranchFilter={selectedBranch}
        chartTitle="Sales Chart"
      />
    </div>
  )
}
